// Shared types for the lesson player and its step renderers. Mirrors what the lesson API routes return.

import type { GradeResult } from '@/prakrit/lib/lessons/grade'
import type { ClientStep } from '@/prakrit/lib/lessons/resolve'

export type StepOf<T extends ClientStep['type']> = Extract<ClientStep, { type: T }>

export type StepAnswer =
    | { id: string }
    | { value: string }
    | { pairs: Record<string, string> }
    | { tiles: string[] }

export interface GradeAttempt {
    ref: string
    skill: string
    correct: boolean
    code?: string
    label?: string
}

/** POST /api/lessons/<id>/start */
export interface StartResponse {
    run: string
    expiresAt: string
}

/** POST /api/lessons/<id>/grade */
export interface GradeResponse extends GradeResult {
    xpAwarded: number
    receipt?: string
    preview?: boolean
    /** Answers the server has counted for this step in this run (this one included). */
    tries: number
    /** The first answer the server counted for this step was right. */
    firstTry: boolean
    /** The step was already settled in this run: nothing new was recorded. */
    repeat: boolean
    attempt: GradeAttempt
}

/** POST /api/lessons/<id>/complete */
export interface CompleteResponse {
    score: number
    passed: boolean
    passMark: number
    xpAwarded: number
    receipt?: string
    unlocks: string[]
    preview?: boolean
    /** This run was already finished: the stored result. */
    repeat?: boolean
}

/** A 409 from grade or complete: the run cannot be used (updated lesson, expired, other account), so start again. */
export interface RestartResponse {
    error: string
    restart: true
    reason: 'missing' | 'other' | 'changed' | 'expired' | 'finished'
}

/** The answer shown after two wrong tries (learn / practice) or after the one try of a test question. */
export interface Reveal {
    expected?: string
    expectedPairs?: Record<string, string>
}

/** What a graded step renderer receives from the player. */
export interface GradedStepProps<S, A> {
    step: S
    /** Unique per mode, step and run; used for shared-layout animation ids. */
    uid: string
    answer: A | null
    onAnswer: (answer: A | null) => void
    result: GradeResponse | null
    /** Inputs are frozen (checking, solved, or a test question already answered). */
    locked: boolean
    reveal: Reveal | null
    /** The question has been answered for good (solved, or answered once in a test). */
    settled: boolean
}

export type GradeTone = 'correct' | 'wrong' | null

export const toneOf = (result: GradeResponse | null): GradeTone => {
    if (!result) return null

    return result.correct ? 'correct' : 'wrong'
}
